/**
 * FiPr.ai Studio View — 3-Panel Production Command Center
 *
 * Layout:
 *  LEFT  (320px) — Scene list with thumbnails, type tags, stats footer
 *  CENTER (flex) — Scene detail with tabs: Overview | Breakdown | Notes | Continuity
 *  RIGHT  (280px) — Fifi AI insights + Ask Fifi chat
 *  BOTTOM (48px)  — Production stats bar
 *
 * Plus: Script Flow Scrubber at top
 */
import React, { useState, useRef, useEffect } from 'react';
import {
  Users, Film, Sparkles, Moon,
  Zap, Bookmark, Send, Layers,
  LayoutGrid, Camera, CloudRain, Eye, Wand2,
  Loader2,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { mlSafeStyle } from '../../utils/mlSafe';
import { MLText } from '../MLText';
import { getBreakdownItemLabel } from '../../utils/breakdownLabel';
import { formatSceneNumber } from '../../utils/sceneNumber';
/* eslint-disable-next-line no-unused-vars */
import { AuthImage } from '../AuthImage'; // FLAGGED (2026-02): dropped-ref — restore <AuthImage path={...}/> in Reference Images strip OR delete this import.
import { ImageUploadButton, LightboxThumb } from '../ImageUpload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Design tokens ─────────────────────────────────────────────────────────────
const S = {
  bg:         'var(--cb-bg)',
  leftBg:     'var(--cb-surface)',
  centerBg:   'var(--cb-surface)',
  rightBg:    'var(--cb-surface)',
  card:       'var(--cb-surface-h)',
  cardHover:  'var(--cb-surface-a)',
  border:     'var(--cb-border)',
  accent:     'var(--cb-accent)',
  accentDim:  'var(--cb-accent-dim)',
  textPri:    'var(--cb-text)',
  textSec:    'var(--cb-text-sec)',
  textDim:    'var(--cb-text-dim)',
};

// ── Scene type config ─────────────────────────────────────────────────────────
const typeConfig = {
  'EXT DAY':        { color: '#F5C518', label: 'DAY',   ltLabel: 'EXT', ltColor: '#34D399', gradient: 'linear-gradient(160deg,#0e1a08 0%,#1a3a12 60%,#2a5a20 100%)' },
  'INT DAY':        { color: '#60A5FA', label: 'DAY',   ltLabel: 'INT', ltColor: '#60A5FA', gradient: 'linear-gradient(160deg,#0a0e1a 0%,#111828 60%,#1a2540 100%)' },
  'EXT NIGHT':      { color: '#8B5CF6', label: 'NIGHT', ltLabel: 'EXT', ltColor: '#34D399', gradient: 'linear-gradient(160deg,#05050e 0%,#0a0518 60%,#150a30 100%)' },
  'INT NIGHT':      { color: '#34D399', label: 'NIGHT', ltLabel: 'INT', ltColor: '#60A5FA', gradient: 'linear-gradient(160deg,#050a10 0%,#0a1520 60%,#0f2030 100%)' },
  'EXT DAWN':       { color: '#FB923C', label: 'DAWN',  ltLabel: 'EXT', ltColor: '#34D399', gradient: 'linear-gradient(160deg,#150800 0%,#3a1508 60%,#6b2a10 100%)' },
  'EXT DUSK':       { color: '#F97316', label: 'DUSK',  ltLabel: 'EXT', ltColor: '#34D399', gradient: 'linear-gradient(160deg,#180500 0%,#3a0f05 60%,#5a1a0a 100%)' },
  'INT CONTINUOUS': { color: '#94A3B8', label: 'CONT',  ltLabel: 'INT', ltColor: '#60A5FA', gradient: 'linear-gradient(160deg,#0a0a12 0%,#141420 100%)' },
  'EXT CONTINUOUS': { color: '#64748B', label: 'CONT',  ltLabel: 'EXT', ltColor: '#34D399', gradient: 'linear-gradient(160deg,#0a0e0a 0%,#141e14 100%)' },
};
const FLAGGED_TYPE = { color: '#F87171', label: 'FX', ltLabel: 'FX', ltColor: '#F87171', gradient: 'linear-gradient(160deg,#1a0505 0%,#2a0808 60%,#3a1010 100%)' };
const getType = (scene) => {
  if (scene.is_flagged) return FLAGGED_TYPE;
  const k = `${(scene.location_type || 'INT').toUpperCase()} ${(scene.time_of_day || 'DAY').toUpperCase()}`;
  return typeConfig[k] || typeConfig['INT DAY'];
};

// ── Dept Readiness ────────────────────────────────────────────────────────────
const DEPT_STATUS = {
  Ready:   { dot: '#34D399', text: '#34D399', bg: 'rgba(52,211,153,0.1)' },
  Pending: { dot: '#FB923C', text: '#FB923C', bg: 'rgba(251,146,60,0.1)' },
  Review:  { dot: '#E8C547', text: '#E8C547', bg: 'rgba(232,197,71,0.1)' },
  Locked:  { dot: '#60A5FA', text: '#60A5FA', bg: 'rgba(96,165,250,0.1)' },
  N_A:     { dot: S.textDim, text: S.textDim, bg: 'rgba(255,255,255,0.04)' },
};

const getDeptReadiness = (scene) => {
  const items = scene.breakdown_items || {};
  return [
    { label: 'ART',     status: (items.ART_SET_DRESSING || []).length > 0 ? 'Ready' : 'Pending' },
    { label: 'COSTUME', status: (items.COSTUME || []).length > 0 ? 'Ready' : 'Pending' },
    { label: 'PROPS',   status: (items.PROP || []).length > 0 ? 'Ready' : 'Pending' },
    { label: 'VFX',     status: (items.VFX || []).length > 0 ? 'Review' : 'N_A' },
    { label: 'CASTING', status: (scene.characters_in_scene || []).length > 0 ? 'Locked' : 'N_A' },
  ];
};

// ── AI Insights generator ─────────────────────────────────────────────────────
function buildInsights(breakdown) {
  const insights = [];
  const extScenes = breakdown.filter(s => (s.location_type || '').toUpperCase() === 'EXT');
  const nightScenes = breakdown.filter(s => (s.time_of_day || '').toUpperCase() === 'NIGHT');

  if (extScenes.length > 10) {
    insights.push({
      id: 'weather', type: 'weather', icon: CloudRain,
      title: 'Weather Risk',
      body: `${extScenes.length} exterior scenes. Plan weather cover (INT alternatives) for rain-prone months.`,
      action: 'View EXT Scenes',
    });
  }

  // Location merge opportunity
  const locCount = {};
  breakdown.forEach(s => { const l = s.location_name || ''; locCount[l] = (locCount[l] || 0) + 1; });
  const topLoc = Object.entries(locCount).sort((a, b) => b[1] - a[1]).filter(([k]) => k)[0];
  if (topLoc && topLoc[1] > 3) {
    insights.push({
      id: 'merge', type: 'schedule', icon: Zap,
      title: 'Schedule Suggestion',
      body: `${topLoc[1]} scenes at ${topLoc[0]}. Grouping them reduces company moves and saves setup cost.`,
      action: 'View Suggestion',
    });
  }

  // Actor utilization
  const actorCount = {};
  breakdown.forEach(s => (s.characters_in_scene || []).forEach(a => actorCount[a] = (actorCount[a] || 0) + 1));
  const topActor = Object.entries(actorCount).sort((a, b) => b[1] - a[1])[0];
  if (topActor) {
    insights.push({
      id: 'actor', type: 'actor', icon: Users,
      title: 'Actor Utilization',
      body: `${topActor[0]} appears in ${topActor[1]} scenes — review for potential holding day gaps.`,
      action: 'See Schedule',
    });
  }

  // Continuity (costumes)
  const costumeScenes = breakdown.filter(s => (s.breakdown_items?.COSTUME || []).length > 0);
  if (costumeScenes.length > 2) {
    insights.push({
      id: 'continuity', type: 'continuity', icon: Eye,
      title: 'Continuity Note',
      body: `${costumeScenes.length} scenes have costume items — cross-check changes for character arcs.`,
      action: 'Check Continuity',
    });
  }

  // Night scenes
  if (nightScenes.length > 5) {
    insights.push({
      id: 'night', type: 'schedule', icon: Moon,
      title: 'Night Shoot Density',
      body: `${nightScenes.length} night scenes. Cluster them to avoid crew turnaround violations (12hr rule).`,
      action: 'View Night Scenes',
    });
  }

  return insights.slice(0, 4);
}

const INSIGHT_COLORS = {
  weather:     { accent: '#60A5FA', bg: 'rgba(96,165,250,0.08)' },
  schedule:    { accent: '#E8C547', bg: 'rgba(232,197,71,0.08)' },
  actor:       { accent: '#FB923C', bg: 'rgba(251,146,60,0.08)' },
  continuity:  { accent: '#8B5CF6', bg: 'rgba(139,92,246,0.08)' },
};

// ── Script Flow Scrubber ──────────────────────────────────────────────────────
const ScriptFlowScrubber = ({ scenes, selectedId, onSelect }) => {
  const total = scenes.length;
  if (total === 0) return null;
  return (
    <div style={{ background: S.leftBg, borderBottom: `1px solid ${S.border}` }} className="px-5 pt-3 pb-2 flex-shrink-0">
      <div className="flex items-center gap-2 mb-1.5">
        <span className="text-[9px] font-bold uppercase tracking-widest" style={{ color: S.textDim, letterSpacing: '0.14em' }}>Script Flow</span>
        <div className="flex items-center gap-2.5 ml-2">
          {[{ c: '#F5C518', l: 'Day' }, { c: '#8B5CF6', l: 'Night' }, { c: '#60A5FA', l: 'Int' }, { c: '#34D399', l: 'Ext' }].map(({ c, l }) => (
            <div key={l} className="flex items-center gap-1">
              <div className="w-1.5 h-1.5 rounded-full" style={{ background: c }} />
              <span className="text-[9px]" style={{ color: S.textDim }}>{l}</span>
            </div>
          ))}
        </div>
      </div>
      {/* Scrubber bar */}
      <div className="relative h-5 flex items-center">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full h-0.5 rounded-full" style={{ background: 'rgba(255,255,255,0.08)' }} />
        </div>
        {/* Scene dots */}
        <div className="relative w-full flex items-center">
          {scenes.map((s, i) => {
            const isNight = (s.time_of_day || '').toUpperCase() === 'NIGHT';
            const dotColor = isNight ? '#8B5CF6' :
              (s.location_type || '').toUpperCase() === 'EXT' ? '#34D399' :
              (s.time_of_day || '').toUpperCase() === 'DAWN' ? '#FB923C' : '#F5C518';
            const isSelected = s.id === selectedId;
            return (
              <div
                key={s.id}
                className="absolute -translate-x-1/2 cursor-pointer transition-transform"
                style={{ left: `${(i / (total - 1)) * 100}%`, transform: 'translateX(-50%)' }}
                onClick={() => onSelect(s)}
                title={`Scene ${formatSceneNumber(s.scene_number)}: ${s.scene_header}`}
              >
                <div
                  className="rounded-full transition-all duration-150"
                  style={{
                    width: isSelected ? 10 : 5,
                    height: isSelected ? 10 : 5,
                    background: isSelected ? S.accent : dotColor,
                    boxShadow: isSelected ? `0 0 6px ${S.accent}` : 'none',
                    opacity: isSelected ? 1 : 0.6,
                  }}
                />
              </div>
            );
          })}
        </div>
      </div>
      {/* Position markers */}
      <div className="flex justify-between mt-0.5">
        {[0, Math.round(total * 0.25), Math.round(total * 0.5), Math.round(total * 0.75), total - 1].map((idx) => (
          <span key={idx} className="text-[9px] tabular-nums" style={{ color: S.textDim }}>{scenes[idx]?.scene_number || idx + 1}</span>
        ))}
      </div>
    </div>
  );
};

// ── Scene List Card ───────────────────────────────────────────────────────────
const SceneListCard = ({ scene, isSelected, onClick }) => {
  const t = getType(scene);
  const items = scene.breakdown_items || {};
  const castCount = (scene.characters_in_scene || []).length;
  const propCount = (items.PROP || []).length;
  const costumeCount = (items.COSTUME || []).length;
  const vfxCount = (items.VFX || []).length;
  const pages = ((scene.page_count_eighths || 0) / 8).toFixed(1);

  return (
    <div
      onClick={onClick}
      className="flex gap-0 cursor-pointer transition-all duration-150 overflow-hidden relative"
      style={{
        background: isSelected ? S.card : 'transparent',
        borderLeft: `3px solid ${isSelected ? t.color : 'transparent'}`,
        borderBottom: `1px solid ${S.border}`,
      }}
      onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; }}
      onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
      data-testid={`studio-scene-${scene.scene_number ?? 'unnumbered'}`}
    >
      {/* Thumbnail */}
      <div className="flex-shrink-0 w-20 h-[90px] relative overflow-hidden" style={{ background: t.gradient }}>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-bold tabular-nums" style={{ color: 'rgba(255,255,255,0.15)' }}>
            {formatSceneNumber(scene.scene_number, { padStart: 2 })}
          </span>
        </div>
        {/* Bookmark icon */}
        <div className="absolute top-1.5 left-1.5">
          <div className="h-3 w-3 rounded-sm flex items-center justify-center" style={{ background: `${t.color}30` }}>
            <Bookmark className="h-2 w-2" style={{ color: t.color }} />
          </div>
        </div>
        {scene.is_flagged && (
          <div className="absolute bottom-1 right-1 h-2 w-2 rounded-full animate-pulse" style={{ background: '#F87171' }} />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 px-3 py-2.5">
        {/* Header row */}
        <div className="flex items-start justify-between mb-1.5">
          <div className="flex-1 min-w-0 pr-2">
            <span className="text-xs font-semibold leading-tight block truncate" style={{ color: S.textPri }}>
              {scene.scene_header}
            </span>
          </div>
          <span className="text-[10px] font-mono flex-shrink-0" style={{ color: S.textDim }}>{pages}p</span>
        </div>

        {/* Tag pills */}
        <div className="flex items-center gap-1 mb-2 flex-wrap">
          <span className="text-[8px] font-bold px-1.5 py-0.5 rounded" style={{ background: `${t.ltColor}18`, color: t.ltColor }}>{t.ltLabel}</span>
          <span className="text-[8px] font-bold px-1.5 py-0.5 rounded" style={{ background: `${t.color}18`, color: t.color }}>{t.label}</span>
          {scene.location_name && (
            <span className="text-[8px] px-1.5 py-0.5 rounded truncate max-w-[80px]" style={{ background: 'rgba(255,255,255,0.05)', color: S.textDim }}>{scene.location_name}</span>
          )}
        </div>

        {/* Stats footer */}
        <div className="flex items-center gap-2.5">
          {castCount > 0 && (
            <div className="flex items-center gap-0.5">
              <Users className="h-2.5 w-2.5" style={{ color: S.textDim }} />
              <span className="text-[9px] tabular-nums" style={{ color: S.textSec }}>{castCount}</span>
            </div>
          )}
          {propCount > 0 && (
            <div className="flex items-center gap-0.5">
              <Film className="h-2.5 w-2.5" style={{ color: S.textDim }} />
              <span className="text-[9px] tabular-nums" style={{ color: S.textSec }}>{propCount}</span>
            </div>
          )}
          {costumeCount > 0 && (
            <div className="flex items-center gap-0.5">
              <Layers className="h-2.5 w-2.5" style={{ color: S.textDim }} />
              <span className="text-[9px] tabular-nums" style={{ color: S.textSec }}>{costumeCount}</span>
            </div>
          )}
          {vfxCount > 0 && (
            <span className="text-[8px] px-1.5 py-0.5 rounded font-semibold" style={{ background: 'rgba(96,165,250,0.12)', color: '#60A5FA' }}>VFX {vfxCount}</span>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Scene Detail Panel ────────────────────────────────────────────────────────
const SceneDetailPanel = ({ scene, getSynopsis, breakdownLanguage }) => {
  const [activeTab, setActiveTab] = useState('overview');
  // Locally-appended reference-photo paths (uploaded from this Studio view
  // without a full server re-fetch). Merged into the display strip below.
  const [localRefPaths, setLocalRefPaths] = useState([]);
  // C4 — AI ref-generation in flight state (spinner + disabled button).
  const [aiGenLoading, setAiGenLoading] = useState(false);
  useEffect(() => { setLocalRefPaths([]); setAiGenLoading(false); }, [scene?.id]);
  const t = getType(scene);
  const deptReadiness = getDeptReadiness(scene);
  const cast = scene.characters_in_scene || [];
  const items = scene.breakdown_items || {};
  const pages = ((scene.page_count_eighths || 0) / 8).toFixed(1);

  // Flatten every breakdown_item across categories to (a) collect existing
  // image_paths for display and (b) pick an anchor item_id to attach new
  // uploads to. Prefers the first item with images (keeps the reference-
  // gallery grouped); otherwise falls back to the first item overall.
  const allItems = Object.values(items).flat().filter(Boolean);
  const serverRefPaths = allItems.flatMap((it) => it.image_paths || []);
  // De-duplicate — multiple breakdown items on the same scene sometimes
  // share a reference (e.g. a hero prop appearing on both a PROP row and
  // a linked SFX row). We want one tile per unique storage path.
  const refPaths = Array.from(new Set([...serverRefPaths, ...localRefPaths]));
  const uploadAnchor =
    allItems.find((it) => (it.image_paths || []).length > 0) ||
    allItems[0] ||
    null;

  // C4 — "Suggest Reference": Nano Banana generation composing scene heading
  // + truncated synopsis into a single reference still. Attaches to the same
  // `uploadAnchor` breakdown item that the upload button targets so the
  // gallery stays coherent.
  const handleSuggestReference = async () => {
    if (!uploadAnchor || aiGenLoading) return;
    setAiGenLoading(true);
    const t = toast.loading('Generating reference image with AI…', {
      description: 'This usually takes 10–30 seconds.',
    });
    try {
      const token = localStorage.getItem('token');
      const heading = scene.scene_header || '';
      const synopsis = (getSynopsis(scene) || '').slice(0, 300);
      const { data } = await axios.post(
        `${API}/breakdown-items/${uploadAnchor.id}/generate-reference`,
        { scene_id: scene.id, heading, synopsis },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 },
      );
      if (data?.storage_path) {
        setLocalRefPaths((prev) => [...prev, data.storage_path]);
        toast.success('Reference image generated', { id: t });
      } else {
        toast.error('Generation returned no image', { id: t });
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Unknown error';
      toast.error('AI generation failed', { id: t, description: String(msg).slice(0, 160) });
    } finally {
      setAiGenLoading(false);
    }
  };

  const TABS = [
    { id: 'overview', label: 'Overview' },
    { id: 'breakdown', label: 'Breakdown' },
    { id: 'notes', label: 'Notes' },
  ];

  const CATEGORIES = [
    { key: 'PROP', label: 'Props' },
    { key: 'COSTUME', label: 'Costume' },
    { key: 'ART_SET_DRESSING', label: 'Art / Set' },
    { key: 'MAKEUP_HAIR', label: 'Makeup & Hair' },
    { key: 'PICTURE_VEHICLE', label: 'Vehicles' },
    { key: 'VFX', label: 'VFX' },
    { key: 'STUNT', label: 'Stunts' },
    { key: 'SFX_SPECIAL_EFFECTS', label: 'SFX' },
    { key: 'SOUND_MUSIC', label: 'Sound' },
    { key: 'PRODUCTION_NOTE', label: 'Production Notes' },
  ];

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-5 pt-4 pb-3 flex-shrink-0" style={{ borderBottom: `1px solid ${S.border}`, borderTop: `2px solid ${t.color}` }}>
        <div className="flex items-start justify-between mb-2">
          <div>
            <div className="text-[10px] font-semibold mb-0.5" style={{ color: S.textDim }}>Scene {formatSceneNumber(scene.scene_number)}</div>
            <h2 className="font-['DM_Serif_Display'] text-lg leading-tight" style={{ color: S.textPri }}>{scene.scene_header}</h2>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Bookmark className="h-3.5 w-3.5" style={{ color: S.textDim }} />
          </div>
        </div>
        {/* Tags */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[9px] font-bold px-2 py-0.5 rounded" style={{ background: `${t.ltColor}18`, color: t.ltColor }}>{t.ltLabel}</span>
          <span className="text-[9px] font-bold px-2 py-0.5 rounded" style={{ background: `${t.color}18`, color: t.color }}>{t.label}</span>
          {scene.location_name && (
            <span className="text-[9px] px-2 py-0.5 rounded" style={{ background: 'rgba(255,255,255,0.06)', color: S.textDim }}>{scene.location_name}</span>
          )}
          <span className="text-[9px] font-mono px-2 py-0.5 rounded" style={{ background: 'rgba(255,255,255,0.04)', color: S.textDim }}>{pages} page</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b flex-shrink-0" style={{ borderColor: S.border }}>
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className="px-4 py-2.5 text-xs font-medium transition-colors relative"
            style={{ color: activeTab === tab.id ? S.accent : S.textDim }}
          >
            {tab.label}
            {activeTab === tab.id && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full" style={{ background: S.accent }} />
            )}
          </button>
        ))}
      </div>

      {/* Content area */}
      <div className="flex-1 overflow-y-auto p-5">
        {activeTab === 'overview' && (
          <div className="space-y-5">
            {/* Synopsis */}
            <div>
              <div className="text-[9px] font-bold uppercase tracking-widest mb-2" style={{ color: S.textDim, letterSpacing: '0.1em' }}>Synopsis</div>
              <p className="text-sm leading-relaxed"
                 style={{ color: S.textSec, ...mlSafeStyle(getSynopsis(scene)) }}>
                {getSynopsis(scene)}
              </p>
            </div>

            {/* Reference Images — aggregated across scene's breakdown items.
                Upload adds to `uploadAnchor` (first item w/ images else first
                item). New paths merge into the strip via localRefPaths so
                the render updates without a full page reload. */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[9px] font-bold uppercase tracking-widest" style={{ color: S.textDim, letterSpacing: '0.1em' }}>Reference Images</div>
                {uploadAnchor ? (
                  <div className="flex items-center gap-1.5">
                    {/* C4 — Suggest Reference (AI) */}
                    <button
                      type="button"
                      onClick={handleSuggestReference}
                      disabled={aiGenLoading}
                      data-testid="studio-suggest-reference-btn"
                      title="Generate a reference image with AI"
                      className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{
                        background: aiGenLoading ? 'rgba(255,255,255,0.04)' : `${S.accent}18`,
                        color: aiGenLoading ? S.textDim : S.accent,
                        border: `1px solid ${S.accent}30`,
                      }}
                    >
                      {aiGenLoading ? (
                        <>
                          <Loader2 className="h-3 w-3 animate-spin" data-testid="studio-suggest-reference-spinner" />
                          <span>Generating…</span>
                        </>
                      ) : (
                        <>
                          <Wand2 className="h-3 w-3" />
                          <span>Suggest</span>
                        </>
                      )}
                    </button>
                    <ImageUploadButton
                      itemId={uploadAnchor.id}
                      onUploaded={(sp) => setLocalRefPaths((prev) => [...prev, sp])}
                    />
                  </div>
                ) : null}
              </div>
              {refPaths.length > 0 ? (
                <div className="grid grid-cols-4 gap-2">
                  {refPaths.slice(0, 4).map((p, i) => (
                    <div
                      key={`${p}-${i}`}
                      className="rounded-lg overflow-hidden aspect-square"
                      style={{ background: t.gradient, border: `1px solid ${S.border}` }}
                      data-testid={`studio-ref-thumb-${i}`}
                    >
                      <LightboxThumb
                        paths={refPaths}
                        alt={`Reference ${i + 1}`}
                        className="w-full h-full object-cover cursor-pointer"
                        galleryImages={refPaths.map((rp, j) => ({ path: rp, alt: `Reference ${j + 1}` }))}
                        galleryStartIndex={i}
                      />
                    </div>
                  ))}
                  {refPaths.length > 4 && (
                    <div className="col-span-4 text-[9px]" style={{ color: S.textDim }}>
                      +{refPaths.length - 4} more reference{refPaths.length - 4 === 1 ? '' : 's'} attached via breakdown items
                    </div>
                  )}
                </div>
              ) : (
                <div className="rounded-xl overflow-hidden relative" style={{ height: 140, background: t.gradient }}>
                  <div className="absolute inset-0 flex items-center justify-center flex-col gap-2" style={{ background: 'rgba(0,0,0,0.3)' }}>
                    <Camera className="h-5 w-5" style={{ color: 'rgba(255,255,255,0.35)' }} />
                    <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.4)' }}>
                      {uploadAnchor ? 'Click camera icon above to upload' : 'Add breakdown items to enable uploads'}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Key Characters */}
            {cast.length > 0 && (
              <div>
                <div className="text-[9px] font-bold uppercase tracking-widest mb-3" style={{ color: S.textDim, letterSpacing: '0.1em' }}>Key Characters</div>
                <div className="flex gap-4 flex-wrap">
                  {cast.slice(0, 4).map((c, i) => (
                    <div key={c} className="flex flex-col items-center gap-1.5">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold" style={{ background: `${t.color}20`, border: `1.5px solid ${t.color}40`, color: t.color }}>
                        {(c[0] || '').toUpperCase()}
                      </div>
                      <div className="text-center">
                        <div className="text-[10px] font-medium leading-tight" style={{ color: S.textPri }}>{c.split(' ')[0]}</div>
                        <div className="text-[9px]" style={{ color: S.textDim }}>{i === 0 ? 'Lead' : i === 1 ? 'Support' : 'Minor'}</div>
                      </div>
                    </div>
                  ))}
                  {cast.length > 4 && (
                    <div className="flex flex-col items-center gap-1.5">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm" style={{ background: 'rgba(255,255,255,0.05)', color: S.textDim }}>
                        +{cast.length - 4}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'breakdown' && (
          <div className="space-y-3">
            {CATEGORIES.map(cat => {
              const catItems = items[cat.key] || [];
              if (catItems.length === 0) return null;
              return (
                <div key={cat.key} className="rounded-xl p-3" style={{ background: S.card, border: `1px solid ${S.border}` }}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: S.textDim, letterSpacing: '0.08em' }}>{cat.label}</span>
                    <span className="text-[10px]" style={{ color: S.textDim }}>({catItems.length})</span>
                  </div>
                  <div className="space-y-1">
                    {catItems.slice(0, 5).map(it => (
                      <div key={it.id} className="flex items-center gap-2">
                        <div className="h-1 w-1 rounded-full flex-shrink-0" style={{ background: S.textDim }} />
                        <span className="text-xs" style={{ color: S.textSec }}>
                          {it.character_name && <span style={{ color: S.textPri }}>{it.character_name}: </span>}
                          <MLText as="span">{getBreakdownItemLabel(it.description, breakdownLanguage === 'english' ? 'english' : 'original')}</MLText>
                        </span>
                      </div>
                    ))}
                    {catItems.length > 5 && <span className="text-[9px]" style={{ color: S.textDim }}>+{catItems.length - 5} more</span>}
                  </div>
                </div>
              );
            })}
            {CATEGORIES.every(cat => !(items[cat.key] || []).length) && (
              <p className="text-sm text-center py-8" style={{ color: S.textDim }}>No breakdown items yet.</p>
            )}
          </div>
        )}

        {activeTab === 'notes' && (
          <div className="space-y-4">
            <div className="rounded-xl p-4" style={{ background: S.card, border: `1px solid ${S.border}` }}>
              <div className="text-[9px] font-bold uppercase tracking-widest mb-2" style={{ color: S.textDim }}>Scene Notes</div>
              <p className="text-xs leading-relaxed" style={{ color: S.textSec }}>
                {scene.is_flagged ? `⚠ FLAGGED: ${scene.flag_reason || 'This scene has special requirements.'}` : 'No special notes for this scene.'}
              </p>
            </div>
            {scene.flag_reason && (
              <div className="rounded-xl p-4" style={{ background: 'rgba(248,113,113,0.06)', border: '1px solid rgba(248,113,113,0.15)' }}>
                <div className="text-[9px] font-bold uppercase mb-1" style={{ color: '#F87171' }}>Production Flag</div>
                <p className="text-xs" style={{ color: '#F87171' }}>{scene.flag_reason}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Department Readiness Bar */}
      <div className="flex-shrink-0 px-4 py-3" style={{ borderTop: `1px solid ${S.border}`, background: S.leftBg }}>
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-[8px] font-bold uppercase tracking-widest" style={{ color: S.textDim, letterSpacing: '0.14em' }}>Department Readiness</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {deptReadiness.filter(d => d.status !== 'N_A').map(dept => {
            const cfg = DEPT_STATUS[dept.status] || DEPT_STATUS.N_A;
            return (
              <div key={dept.label} className="flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg" style={{ background: cfg.bg }}>
                <span className="text-[8px] font-bold uppercase" style={{ color: cfg.text, letterSpacing: '0.06em' }}>{dept.label}</span>
                <div className="flex items-center gap-1">
                  <div className="h-1.5 w-1.5 rounded-full" style={{ background: cfg.dot }} />
                  <span className="text-[9px]" style={{ color: cfg.text }}>{dept.status}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ── Fifi AI Panel ─────────────────────────────────────────────────────────────
const FifiAIPanel = ({ insights, projectId, token, breakdown }) => {
  const [tab, setTab] = useState('insights');
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([{
    role: 'assistant',
    content: "Hi! I'm Fifi AI — your production intelligence assistant. I've analyzed your full breakdown. Ask me anything or check my insights.",
  }]);
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, loading]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/projects/${projectId}/ai-chat`,
        { message: msg, history: messages.map(m => ({ role: m.role, content: m.content })) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages(prev => [...prev, { role: 'assistant', content: data.reply }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, something went wrong.' }]);
    } finally { setLoading(false); }
  };

  return (
    <div className="flex flex-col h-full" style={{ background: S.rightBg, borderLeft: `1px solid ${S.border}` }}>
      {/* Header */}
      <div className="px-4 pt-4 pb-3 flex-shrink-0" style={{ borderBottom: `1px solid ${S.border}` }}>
        <div className="flex items-center gap-2 mb-0.5">
          <Sparkles className="h-4 w-4" style={{ color: S.accent }} />
          <span className="font-['DM_Serif_Display'] text-base" style={{ color: S.textPri }}>Fifi AI</span>
        </div>
        <div className="text-[10px]" style={{ color: S.textDim }}>Your Production Assistant</div>
      </div>

      {/* Tabs */}
      <div className="flex border-b flex-shrink-0" style={{ borderColor: S.border }}>
        {['insights', 'ask'].map(t => (
          <button key={t} onClick={() => setTab(t)} className="flex-1 py-2.5 text-xs font-medium transition-colors relative" style={{ color: tab === t ? S.accent : S.textDim }}>
            {t === 'insights' ? 'Insights' : 'Ask Fifi'}
            {tab === t && <div className="absolute bottom-0 left-0 right-0 h-0.5" style={{ background: S.accent }} />}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {tab === 'insights' && (
          <div className="p-4 space-y-3">
            {insights.map(ins => {
              const cfg = INSIGHT_COLORS[ins.type] || INSIGHT_COLORS.schedule;
              const Icon = ins.icon;
              return (
                <div key={ins.id} className="rounded-xl p-3.5" style={{ background: cfg.bg, border: `1px solid rgba(255,255,255,0.06)` }}>
                  <div className="flex items-start gap-2 mb-2">
                    <div className="h-5 w-5 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: `${cfg.accent}20` }}>
                      <Icon className="h-2.5 w-2.5" style={{ color: cfg.accent }} />
                    </div>
                    <div>
                      <div className="text-xs font-semibold" style={{ color: cfg.accent }}>{ins.title}</div>
                      <p className="text-[11px] mt-0.5 leading-relaxed" style={{ color: S.textSec }}>{ins.body}</p>
                    </div>
                  </div>
                  <button className="text-[10px] font-semibold" style={{ color: cfg.accent }}>{ins.action} →</button>
                </div>
              );
            })}
            {insights.length === 0 && (
              <div className="text-center py-8">
                <Sparkles className="h-5 w-5 mx-auto mb-2" style={{ color: S.textDim, opacity: 0.3 }} />
                <p className="text-xs" style={{ color: S.textDim }}>No insights yet. Add scenes to your breakdown.</p>
              </div>
            )}
          </div>
        )}

        {tab === 'ask' && (
          <div className="flex flex-col h-full">
            <div className="flex-1 p-4 space-y-3 overflow-y-auto">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[88%] rounded-xl px-3 py-2 text-xs ${m.role === 'user' ? 'rounded-br-sm' : 'rounded-bl-sm'}`}
                    style={{ background: m.role === 'user' ? S.accent : S.card, color: m.role === 'user' ? '#000' : S.textSec, border: m.role !== 'user' ? `1px solid ${S.border}` : 'none' }}>
                    {m.content.split('\n').map((l, li) => <p key={li} className={li > 0 ? 'mt-1' : ''}>{l}</p>)}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="rounded-xl rounded-bl-sm px-3 py-2" style={{ background: S.card, border: `1px solid ${S.border}` }}>
                    <div className="flex gap-1">{[0, 150, 300].map(d => <span key={d} className="h-1.5 w-1.5 rounded-full animate-bounce" style={{ background: S.accent, animationDelay: `${d}ms` }} />)}</div>
                  </div>
                </div>
              )}
              <div ref={endRef} />
            </div>
          </div>
        )}
      </div>

      {/* Chat input (always shown in ask tab) */}
      {tab === 'ask' && (
        <div className="px-3 pb-3 pt-2 flex-shrink-0" style={{ borderTop: `1px solid ${S.border}` }}>
          <div className="flex gap-2">
            <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && send()} placeholder="Ask about your breakdown…" className="flex-1 text-xs px-3 py-2 rounded-lg outline-none" style={{ background: S.card, color: S.textPri, border: `1px solid ${S.border}` }} />
            <button onClick={send} disabled={!input.trim() || loading} className="h-8 w-8 rounded-lg flex items-center justify-center transition-all" style={{ background: S.accent, color: '#000' }}>
              <Send className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* Optimize Schedule button */}
      <div className="px-3 pb-3 flex-shrink-0" style={{ borderTop: tab === 'insights' ? `1px solid ${S.border}` : 'none' }}>
        {tab === 'insights' && (
          <button className="w-full py-3 rounded-xl flex items-center justify-center gap-2 font-semibold text-sm transition-all" style={{ background: S.accent, color: '#000' }}>
            <Sparkles className="h-4 w-4" />
            Optimize Schedule
          </button>
        )}
      </div>
    </div>
  );
};

// ── Bottom Stats Bar ──────────────────────────────────────────────────────────
const BottomStatsBar = ({ breakdown, navigate, projectId }) => {
  const totalEighths = breakdown.reduce((s, sc) => s + (sc.page_count_eighths || 0), 0);
  const totalPages = totalEighths / 8;
  const intPages = Math.floor(totalPages);
  const eighthRem = totalEighths % 8;
  const nightCount = breakdown.filter(s => (s.time_of_day || '').toUpperCase() === 'NIGHT').length;
  const intCount = breakdown.filter(s => (s.location_type || '').toUpperCase() === 'INT').length;
  const extCount = breakdown.filter(s => (s.location_type || '').toUpperCase() === 'EXT').length;
  const estDays = Math.ceil(totalPages / 8);
  const avgPages = estDays > 0 ? totalPages / estDays : 0;

  return (
    <div className="flex items-center justify-between px-5 py-3 flex-shrink-0" style={{ background: S.leftBg, borderTop: `1px solid ${S.border}`, minHeight: 52 }}>
      <div className="flex items-center gap-6 flex-wrap">
        {[
          { label: 'Total Pages', value: `${intPages} ${eighthRem > 0 ? `${eighthRem}/8` : ''}`.trim() },
          { label: 'Est. Shoot Days', value: estDays },
          { label: 'Avg Pages / Day', value: `${Math.floor(avgPages)} ${Math.round((avgPages % 1) * 8)}/8` },
          { label: 'Night Scenes', value: nightCount },
          { label: 'Int / Ext', value: `${intCount} / ${extCount}` },
        ].map(({ label, value }) => (
          <div key={label}>
            <div className="text-[9px] uppercase tracking-widest mb-0.5" style={{ color: S.textDim, letterSpacing: '0.1em' }}>{label}</div>
            <div className="text-sm font-bold tabular-nums" style={{ color: S.textPri }}>{value}</div>
          </div>
        ))}
      </div>
      <button
        onClick={() => navigate && navigate(`/projects/${projectId}/schedule`)}
        className="flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-xs transition-all"
        style={{ background: S.accentDim, color: S.accent, border: `1px solid rgba(232,197,71,0.2)` }}
      >
        <LayoutGrid className="h-3.5 w-3.5" />
        View Strip Board
      </button>
    </div>
  );
};

// ── Main StudioView ───────────────────────────────────────────────────────────
export default function StudioView({ breakdown, projectId, token, getSynopsis, breakdownLanguage, navigate }) {
  const [selectedId, setSelectedId] = useState(breakdown[0]?.id || null);
  const selectedScene = breakdown.find(s => s.id === selectedId) || breakdown[0];
  const insights = React.useMemo(() => buildInsights(breakdown), [breakdown]);
  const [search, setSearch] = useState('');

  const filtered = breakdown.filter(s =>
    !search || s.scene_header.toLowerCase().includes(search.toLowerCase()) ||
    (s.location_name || '').toLowerCase().includes(search.toLowerCase()) ||
    String(s.scene_number ?? '').includes(search)
  );

  return (
    <div className="flex flex-col" style={{ background: S.bg, height: 'calc(100vh - 73px)' }}>
      {/* Script Flow Scrubber */}
      <ScriptFlowScrubber scenes={breakdown} selectedId={selectedId} onSelect={s => setSelectedId(s.id)} />

      {/* Search bar */}
      <div className="px-5 py-2.5 flex-shrink-0" style={{ background: S.leftBg, borderBottom: `1px solid ${S.border}` }}>
        <div className="flex items-center gap-2 max-w-xs">
          <div className="relative flex-1">
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search scenes, characters, props…"
              className="w-full text-xs px-3 py-1.5 rounded-lg outline-none pl-8"
              style={{ background: S.card, color: S.textPri, border: `1px solid ${S.border}` }}
            />
            <div className="absolute left-2.5 top-1/2 -translate-y-1/2">
              <svg className="h-3 w-3" style={{ color: S.textDim }} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
            </div>
          </div>
        </div>
      </div>

      {/* 3-column main area */}
      <div className="flex flex-1 overflow-hidden">
        {/* LEFT: Scene list */}
        <div className="flex-shrink-0 overflow-y-auto" style={{ width: 300, background: S.leftBg, borderRight: `1px solid ${S.border}` }}>
          {filtered.map(scene => (
            <SceneListCard
              key={scene.id}
              scene={scene}
              isSelected={scene.id === selectedId}
              onClick={() => setSelectedId(scene.id)}
            />
          ))}
          {filtered.length === 0 && (
            <div className="py-12 text-center">
              <p className="text-xs" style={{ color: S.textDim }}>No scenes match.</p>
            </div>
          )}
        </div>

        {/* CENTER: Scene detail */}
        <div className="flex-1 overflow-hidden" style={{ background: S.centerBg }}>
          {selectedScene ? (
            <SceneDetailPanel scene={selectedScene} getSynopsis={getSynopsis} breakdownLanguage={breakdownLanguage} />
          ) : (
            <div className="flex items-center justify-center h-full">
              <p style={{ color: S.textDim }} className="text-sm">Select a scene to view details</p>
            </div>
          )}
        </div>

        {/* RIGHT: Fifi AI */}
        <div className="flex-shrink-0" style={{ width: 280 }}>
          <FifiAIPanel insights={insights} projectId={projectId} token={token} breakdown={breakdown} />
        </div>
      </div>

      {/* Bottom stats bar */}
      <BottomStatsBar breakdown={breakdown} navigate={navigate} projectId={projectId} />
    </div>
  );
}
